/**
 * 도움말 관리 페이지
 *
 * - 드래그앤드랍으로 순서 변경 (HTML5 native DnD)
 * - 인라인 모달로 추가/수정
 * - 공개/숨김 토글, 삭제
 */
import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { helpService } from "../../services/helpService";
import { HelpItem } from "../../types/help";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorAlert from "../../components/ui/ErrorAlert";
import StatsCard from "../../components/ui/StatsCard";
import ConfirmModal from "../../components/ui/ConfirmModal";

/* ── 폼 초기값 ────────────────────────────────────────────────────────────── */
const EMPTY_FORM = {
  name: "",
  youtubeUrl: "",
  title: "",
  description: "",
  sortOrder: 0,
  isVisible: true,
};

/* ── 유튜브 ID 추출 (썸네일 미리보기용) ─────────────────────────────────── */
function extractYoutubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[1] : null;
}

/* ── URL에 프로토콜이 없으면 https:// 붙여줌 ─────────────────────────────── */
function safeUrl(url: string): string {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

export default function HelpList() {
  const queryClient = useQueryClient();

  /* ── 데이터 로딩 ────────────────────────────────────────────────────────── */
  const {
    data: rawItems = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["helpItems"],
    queryFn: helpService.getAllItems,
  });

  /* ── 로컬 순서 상태 (드래그 중 즉시 반영) ─────────────────────────────── */
  const [items, setItems] = useState<HelpItem[]>([]);
  const [initialized, setInitialized] = useState(false);
  if (!initialized && rawItems.length > 0) {
    setItems([...rawItems].sort((a, b) => a.sortOrder - b.sortOrder));
    setInitialized(true);
  }
  // rawItems 변경 시 동기화
  const prevRaw = useRef<HelpItem[]>([]);
  if (prevRaw.current !== rawItems && rawItems.length > 0) {
    prevRaw.current = rawItems;
    setItems([...rawItems].sort((a, b) => a.sortOrder - b.sortOrder));
  }

  /* ── 모달 상태 ─────────────────────────────────────────────────────────── */
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<HelpItem | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<HelpItem | null>(null);

  /* ── 드래그앤드랍 상태 ─────────────────────────────────────────────────── */
  const dragIndex = useRef<number | null>(null);

  /* ── Mutations ─────────────────────────────────────────────────────────── */
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["helpItems"] });

  const createMutation = useMutation({
    mutationFn: helpService.createItem,
    onSuccess: () => {
      invalidate();
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Omit<HelpItem, "id" | "createdDt" | "updatedDt">;
    }) => helpService.updateItem(id, data),
    onSuccess: () => {
      invalidate();
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: helpService.deleteItem,
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
    },
  });

  const reorderMutation = useMutation({
    mutationFn: helpService.reorder,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ item }: { item: HelpItem }) =>
      helpService.updateItem(item.id, { ...item, isVisible: !item.isVisible }),
    onSuccess: () => invalidate(),
  });

  /* ── 모달 열기/닫기 ─────────────────────────────────────────────────────── */
  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM, sortOrder: items.length });
    setModalOpen(true);
  };

  const openEdit = (item: HelpItem) => {
    setEditTarget(item);
    setForm({
      name: item.name,
      youtubeUrl: item.youtubeUrl ?? "",
      title: item.title ?? "",
      description: item.description ?? "",
      sortOrder: item.sortOrder,
      isVisible: item.isVisible,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
    setForm(EMPTY_FORM);
  };

  /* ── 저장 ───────────────────────────────────────────────────────────────── */
  const handleSave = () => {
    const payload = {
      ...form,
      youtubeUrl: form.youtubeUrl || null,
      title: form.title || null,
      description: form.description || null,
    };
    if (editTarget) {
      updateMutation.mutate({ id: editTarget.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  /* ── 드래그앤드랍 ────────────────────────────────────────────────────────── */
  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === index) return;
    const next = [...items];
    const [moved] = next.splice(dragIndex.current, 1);
    next.splice(index, 0, moved);
    dragIndex.current = index;
    setItems(next);
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
    // sort_order 재계산 후 서버 저장
    const reordered = items.map((item, i) => ({ ...item, sortOrder: i }));
    setItems(reordered);
    reorderMutation.mutate(
      reordered.map((item) => ({ id: item.id, sortOrder: item.sortOrder })),
    );
  };

  /* ── 통계 ────────────────────────────────────────────────────────────────── */
  const totalCount = items.length;
  const visibleCount = items.filter((i) => i.isVisible).length;
  const hiddenCount = items.filter((i) => !i.isVisible).length;

  const isPending = createMutation.isPending || updateMutation.isPending;

  /* ── 렌더 ────────────────────────────────────────────────────────────────── */
  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert />;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">도움말 관리</h1>
          <p className="text-gray-500 mt-2">앱 도움말 항목을 관리합니다</p>
        </div>
        <button
          onClick={openCreate}
          className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all shadow-md flex items-center space-x-2"
        >
          <i className="fas fa-plus" />
          <span>항목 추가</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatsCard
          label="전체 항목"
          value={totalCount}
          icon="fas fa-question-circle"
          borderColor="border-primary"
          iconColor="text-primary"
          bgColor="bg-primary/10"
        />
        <StatsCard
          label="공개"
          value={visibleCount}
          icon="fas fa-eye"
          borderColor="border-green-500"
          iconColor="text-green-500"
          bgColor="bg-green-500/10"
          valueColor="text-green-600"
        />
        <StatsCard
          label="숨김"
          value={hiddenCount}
          icon="fas fa-eye-slash"
          borderColor="border-gray-400"
          iconColor="text-gray-400"
          bgColor="bg-gray-400/10"
          valueColor="text-gray-600"
        />
      </div>

      {/* 드래그 안내 */}
      {items.length > 1 && (
        <div className="mb-3 flex items-center gap-2 text-xs text-gray-400">
          <i className="fas fa-grip-vertical" />
          <span>
            왼쪽 핸들을 드래그해서 순서를 변경하세요. 놓으면 자동 저장됩니다.
          </span>
          {reorderMutation.isPending && (
            <span className="text-primary font-medium flex items-center gap-1">
              <i className="fas fa-circle-notch fa-spin" /> 저장 중...
            </span>
          )}
        </div>
      )}

      {/* 목록 */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {items.length === 0 ? (
          <div className="p-16 text-center">
            <i className="fas fa-question-circle text-8xl mb-6 text-gray-200" />
            <p className="text-xl font-semibold text-gray-600 mb-2">
              등록된 도움말 항목이 없습니다
            </p>
            <p className="text-gray-400">
              항목 추가 버튼을 눌러 첫 번째 항목을 만들어보세요
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="w-10 px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
                <th className="w-8 px-2 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  메뉴명
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  제목
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  유튜브
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  공개
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map((item, index) => {
                const ytId = item.youtubeUrl
                  ? extractYoutubeId(item.youtubeUrl)
                  : null;
                return (
                  <tr
                    key={item.id}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className="hover:bg-gray-50/70 transition-colors group"
                  >
                    {/* 드래그 핸들 */}
                    <td className="px-4 py-4">
                      <i className="fas fa-grip-vertical text-gray-300 group-hover:text-gray-400 cursor-grab active:cursor-grabbing transition-colors" />
                    </td>

                    {/* 순서 번호 */}
                    <td className="px-2 py-4">
                      <span className="text-xs text-gray-400 tabular-nums">
                        {index + 1}
                      </span>
                    </td>

                    {/* 메뉴명 */}
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center px-3 py-1 bg-primary/10 text-primary text-sm font-semibold rounded-lg">
                        {item.name}
                      </span>
                    </td>

                    {/* 제목 */}
                    <td className="px-4 py-4">
                      {item.title ? (
                        <span className="text-sm text-gray-700 font-medium">
                          {item.title}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300 italic">
                          없음
                        </span>
                      )}
                    </td>

                    {/* 유튜브 */}
                    <td className="px-4 py-4">
                      {ytId ? (
                        <a
                          href={safeUrl(item.youtubeUrl!)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-2 text-sm text-text-red hover:underline"
                        >
                          <i className="fab fa-youtube text-base" />
                          <img
                            src={`https://img.youtube.com/vi/${ytId}/default.jpg`}
                            alt="썸네일"
                            className="w-14 h-10 object-cover rounded"
                          />
                        </a>
                      ) : (
                        <span className="text-xs text-gray-300 italic">
                          없음
                        </span>
                      )}
                    </td>

                    {/* 공개 토글 */}
                    <td className="px-4 py-4 text-center">
                      <button
                        onClick={() => toggleMutation.mutate({ item })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          item.isVisible ? "bg-primary" : "bg-gray-200"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            item.isVisible ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>

                    {/* 편집/삭제 */}
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openEdit(item)}
                          className="px-3 py-1.5 text-xs bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors font-medium"
                        >
                          <i className="fas fa-pen mr-1" />
                          편집
                        </button>
                        <button
                          onClick={() => setDeleteTarget(item)}
                          className="px-3 py-1.5 text-xs bg-text-red/10 text-text-red rounded-lg hover:bg-text-red/20 transition-colors font-medium"
                        >
                          <i className="fas fa-trash mr-1" />
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── 추가/편집 모달 ─────────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeModal}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* 모달 헤더 */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  {editTarget ? "도움말 항목 수정" : "도움말 항목 추가"}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  메뉴명은 필수입니다. 나머지는 선택 항목입니다.
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times text-lg" />
              </button>
            </div>

            {/* 모달 바디 */}
            <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* 메뉴명 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  메뉴명 <span className="text-text-red">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="기본, 등록, 설정, 클릭 ..."
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
              </div>

              {/* 유튜브 URL */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  유튜브 URL{" "}
                  <span className="text-gray-400 font-normal">(선택)</span>
                </label>
                <input
                  type="url"
                  value={form.youtubeUrl}
                  onChange={(e) =>
                    setForm({ ...form, youtubeUrl: e.target.value })
                  }
                  placeholder="https://youtu.be/xxxxxx"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
                {/* 유튜브 미리보기 */}
                {form.youtubeUrl && extractYoutubeId(form.youtubeUrl) && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-gray-100 aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${extractYoutubeId(safeUrl(form.youtubeUrl))}`}
                      className="w-full h-full"
                      allowFullScreen
                      title="미리보기"
                    />
                  </div>
                )}
              </div>

              {/* 제목 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  제목{" "}
                  <span className="text-gray-400 font-normal">
                    (선택 · 영상 아래 굵은 글씨)
                  </span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="기본 사용법 가이드"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
              </div>

              {/* 설명 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  설명 <span className="text-gray-400 font-normal">(선택)</span>
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="기기 연결 후 가장 먼저 사용하게 될 기본 동작입니다."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
                />
              </div>

              {/* 공개 여부 */}
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-semibold text-gray-700">
                    공개 여부
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    앱에서 이 항목을 표시합니다
                  </p>
                </div>
                <button
                  onClick={() =>
                    setForm({ ...form, isVisible: !form.isVisible })
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.isVisible ? "bg-primary" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      form.isVisible ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* 모달 푸터 */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim() || isPending}
                className="px-5 py-2 text-sm text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isPending ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin" />
                    <span>저장 중...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-check" />
                    <span>{editTarget ? "수정 완료" : "추가"}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 삭제 확인 모달 ─────────────────────────────────────────────────── */}
      {deleteTarget && (
        <ConfirmModal
          title={`"${deleteTarget.name}" 항목을 삭제하시겠습니까?`}
          description="삭제된 항목은 복구할 수 없습니다."
          confirmLabel="삭제"
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
